import {LocationQuery, RouteLocationNormalized, RouteLocationRaw} from 'vue-router'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {CollectableResourceTypeSet} from '@toolify/server/src/services/CollectionService/constants/CollectableResourceTypeSet'
import {ClientRouteParamType} from '@toolify/client/src/setup/router/enum/ClientRouteParamType'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {i18n} from '@toolify/client/src/setup/i18n'
import {getResourceTypePluralLabel} from '@toolify/client/src/modules/importTemplate/functions/getTemplateContentTypeOptions'

const collectionLabelKeyByType = new Map<ResourceType, string>([
  [ResourceType.CallRoom, 'resourcesPage.collections.calls'],
  [ResourceType.Cron, 'resourcesPage.collections.crons'],
  [ResourceType.WebhookListener, 'resourcesPage.collections.webhooks'],
  [ResourceType.Connection, 'resourcesPage.collections.connections'],
])

export function getResourceCollectionLabel(resourceType: ResourceType): string {
  const labelKey = collectionLabelKeyByType.get(resourceType)
  if(labelKey) {
    return i18n.global.t(labelKey)
  }
  const pluralLabel = getResourceTypePluralLabel(resourceType)
  return pluralLabel === resourceType ? resourceType : pluralLabel
}

const resourceCollectionPathByType = new Map<ResourceType, string>([
  [ResourceType.Database, 'databases'],
  [ResourceType.Document, 'documents'],
  [ResourceType.Whiteboard, 'whiteboards'],
  [ResourceType.Dashboard, 'dashboards'],
  [ResourceType.Conversation, 'conversations'],
  [ResourceType.CallRoom, 'calls'],
  [ResourceType.Skill, 'skills'],
  [ResourceType.Agent, 'agents'],
  [ResourceType.Automation, 'automations'],
  [ResourceType.Folder, 'folders'],
  [ResourceType.Cron, 'crons'],
  [ResourceType.WebhookListener, 'webhooks'],
  [ResourceType.Connection, 'connections'],
  [ResourceType.App, 'apps'],
])

const resourceTypeByCollectionPath = new Map<string, ResourceType>(
  [...resourceCollectionPathByType.entries()].map(([resourceType, path]) => [path, resourceType]),
)

export const resourceCollectionPaths = [...resourceCollectionPathByType.values()]

export function getResourceCollectionPath(resourceType: ResourceType): string | null {
  if(!CollectableResourceTypeSet.has(resourceType)) {
    return null
  }
  return resourceCollectionPathByType.get(resourceType) ?? null
}

export function getResourceTypeFromCollectionPath(path: string): ResourceType | null {
  return resourceTypeByCollectionPath.get(path) ?? null
}

export function getResourceCollectionLocation(
  workspaceName: string,
  resourceType: ResourceType | null,
  query: LocationQuery = {},
): RouteLocationRaw {
  const nextQuery = {...query}
  delete nextQuery.type
  if(!resourceType) {
    return {
      name: RouteName.WorkspaceResources,
      params: {
        [ClientRouteParamType.WorkspaceName]: workspaceName,
      },
      query: nextQuery,
    }
  }
  const resourceCollection = getResourceCollectionPath(resourceType)
  if(!resourceCollection) {
    return {
      name: RouteName.WorkspaceResources,
      params: {
        [ClientRouteParamType.WorkspaceName]: workspaceName,
      },
      query: nextQuery,
    }
  }
  return {
    name: RouteName.WorkspaceResourceCollection,
    params: {
      [ClientRouteParamType.WorkspaceName]: workspaceName,
      [ClientRouteParamType.ResourceCollection]: resourceCollection,
    },
    query: nextQuery,
  }
}

export function redirectLegacyResourceTypeQuery(to: RouteLocationNormalized): true | RouteLocationRaw {
  const rawType = Array.isArray(to.query.type) ? to.query.type[0] : to.query.type
  if(typeof rawType !== 'string' || !rawType) {
    return true
  }
  const workspaceName = to.params[ClientRouteParamType.WorkspaceName]
  const resourceType = rawType.split(',')[0] as ResourceType
  return getResourceCollectionLocation(
    typeof workspaceName === 'string' ? workspaceName : '',
    getResourceCollectionPath(resourceType) ? resourceType : null,
    to.query,
  )
}
